import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Pizza, ArrowRight, CheckCircle2, ShieldCheck, Upload, Package, Wrench, AlertTriangle, Loader2 } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "../lib/api";
import { Trans, useTranslation } from "react-i18next";
import LanguageSwitcher from "../components/LanguageSwitcher";

const CATEGORY_SUGGESTIONS = {
  insumos: [
    "Lácteos", "Carnes", "Bebidas", "Empaques", "Desechables",
    "Abarrotes", "Verduras y frutas", "Limpieza", "Otros",
  ],
  servicios: [
    "Mantenimiento general", "Fumigación / Control de plagas", "A/C y refrigeración",
    "Vidrios y aluminio", "Electricidad", "Construcción / Obra civil",
    "Tratamiento de aguas / Trampa de grasa", "Extintores y seguridad",
    "Limpieza profesional", "Otros",
  ],
};

const MODALITY_OPTIONS = ["por_visita", "contrato_mensual", "por_evento", "cotizacion_individual"];
const RESPONSE_OPTIONS = ["24h", "48h", "72h", "semanal", "mensual"];

const DOC_FIELDS = [
  { type: "csf", required: true },
  { type: "official_id", required: true },
  { type: "proof_of_address", required: true },
  { type: "bank_statement", required: true },
  { type: "sat_compliance", required: false },
  { type: "acta_constitutiva", required: false, hint: true },
  { type: "poder_legal", required: false, hint: true },
];

function PublicShell({ children }) {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-[#FFDF00] flex items-center justify-center">
            <Pizza className="w-6 h-6 text-[#1A1A1A]" strokeWidth={2.5} />
          </div>
          <div className="leading-tight">
            <div className="font-display font-extrabold text-[#1A1A1A] text-base">Chizy Chiz</div>
            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("supplierReg.headerTitle")}</div>
          </div>
        </div>
        <LanguageSwitcher variant="topbar" />
      </div>
      {children}
    </div>
  );
}

export default function SupplierCompletion() {
  const { token } = useParams();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // "invalid" | "expired"
  const [pre, setPre] = useState(null);
  const [step, setStep] = useState(1);
  const [success, setSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    rfc: "", tax_address: "", category: "",
    contact_email: "", contact_phone: "",
    bank_name: "", bank_account: "", credit_days: 0,
    service_modality: "", service_response_time: "",
    nda_accepted: false,
  });
  const [docs, setDocs] = useState({});
  const [privacyNotice, setPrivacyNotice] = useState(null);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [operatesDifferently, setOperatesDifferently] = useState(null); // null | false | true
  const [branchOpsForm, setBranchOpsForm] = useState({}); // { [branch_code]: {vendor_name, ...} }

  useEffect(() => {
    api.get("/privacy-notice/current").then((r) => setPrivacyNotice(r.data)).catch(() => setPrivacyNotice(null));
  }, []);

  useEffect(() => {
    let active = true;
    api.get(`/public/suppliers/completion/${token}`)
      .then((r) => {
        if (!active) return;
        const d = r.data;
        setPre(d);
        setForm((f) => ({
          ...f,
          rfc: d.rfc || "",
          tax_address: d.tax_address || "",
          contact_email: d.contact_email || "",
          contact_phone: d.contact_phone || "",
          category: d.category_hint || "",
          credit_days: d.credit_days || 0,
          service_modality: d.service_modality || "",
          service_response_time: d.service_response_time || "",
        }));
      })
      .catch((e) => {
        if (!active) return;
        setError(e?.response?.status === 410 ? "expired" : "invalid");
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [token]);

  const isServicios = pre?.supplier_type === "servicios";
  const needsServiceFields = isServicios && (!pre?.service_modality || !pre?.service_response_time);
  const serviceFieldsOk = !needsServiceFields || (form.service_modality && form.service_response_time);
  const mandatoryUploaded = ["csf", "official_id", "proof_of_address", "bank_statement"].every((tp) => docs[tp]?.id);
  const multiBranch = (pre?.branches || []).length > 1;
  const branchOpsOk = operatesDifferently !== null || !multiBranch;

  const setBranchOpField = (code, field, value) =>
    setBranchOpsForm((prev) => ({ ...prev, [code]: { ...(prev[code] || {}), [field]: value } }));

  const fileToDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

  const handleDocChange = async (docType, file) => {
    if (!file) return;
    const MAX_SIZE_MB = 15;
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      toast.error(t("supplierReg.docTooLargeMsg", { size: (file.size / 1024 / 1024).toFixed(1), max: MAX_SIZE_MB }));
      return;
    }
    const okTypes = ["application/pdf", "image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    const okExts = ["pdf", "jpg", "jpeg", "png", "webp", "heic", "heif"];
    if (!okTypes.includes(file.type) && !okExts.includes(ext)) {
      toast.error(t("supplierReg.docBadTypeMsg", { name: file.name }));
      return;
    }
    setDocs((d) => ({ ...d, [docType]: { file, uploading: true } }));
    try {
      const data = await fileToDataUrl(file);
      const r = await api.post("/public/suppliers/upload-document", { doc_type: docType, filename: file.name, data });
      setDocs((d) => ({ ...d, [docType]: { file, id: r.data.id, uploading: false } }));
    } catch (e) {
      const backendDetail = e?.response?.data?.detail;
      toast.error(backendDetail
        ? t("supplierReg.docUploadErrorDetail", { name: file.name, detail: backendDetail })
        : t("supplierReg.docUploadErrorGeneric", { name: file.name }));
      setDocs((d) => { const n = { ...d }; delete n[docType]; return n; });
    }
  };

  const submit = async () => {
    if (!form.nda_accepted) { toast.error(t("supplierReg.ndaRequired")); return; }
    if (privacyNotice && !privacyAccepted) { toast.error(t("supplierReg.privacyRequired")); return; }
    if (!serviceFieldsOk) { toast.error(t("supplierReg.serviceFieldsRequired")); setStep(3); return; }
    if (!mandatoryUploaded) { toast.error(t("supplierReg.docsRequired")); setStep(4); return; }
    setSubmitting(true);
    try {
      const branchOperations = operatesDifferently
        ? (pre.branches || [])
            .filter((b) => branchOpsForm[b.code]?.vendor_name || branchOpsForm[b.code]?.bank_name)
            .map((b) => ({ branch_code: b.code, ...branchOpsForm[b.code] }))
        : [];
      await api.post(`/public/suppliers/completion/${token}`, {
        rfc: form.rfc,
        tax_address: form.tax_address,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        bank_name: form.bank_name,
        bank_account: form.bank_account,
        nda_accepted: form.nda_accepted,
        csf_document_id: docs.csf?.id,
        official_id_document_id: docs.official_id?.id,
        proof_of_address_document_id: docs.proof_of_address?.id,
        bank_statement_document_id: docs.bank_statement?.id,
        sat_compliance_document_id: docs.sat_compliance?.id || null,
        acta_constitutiva_document_id: docs.acta_constitutiva?.id || null,
        poder_legal_document_id: docs.poder_legal?.id || null,
        service_modality: needsServiceFields ? form.service_modality : null,
        service_response_time: needsServiceFields ? form.service_response_time : null,
        privacy_notice_id: privacyNotice?.id || null,
        operates_differently_by_branch: !!operatesDifferently,
        branch_operations: branchOperations.length > 0 ? branchOperations : null,
      });
      setSuccess(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("supplierReg.registerError"));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <PublicShell>
        <div className="max-w-md mx-auto px-6 py-24 text-center">
          <Loader2 className="w-8 h-8 text-slate-400 animate-spin mx-auto" />
          <p className="text-sm text-slate-500 mt-4">{t("supplierCompletion.loadingData")}</p>
        </div>
      </PublicShell>
    );
  }

  if (error) {
    const isExpired = error === "expired";
    return (
      <PublicShell>
        <div className="max-w-md mx-auto px-6 py-20">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="completion-error"
            className="bg-white rounded-3xl border border-slate-200 p-8 text-center">
            <div className="w-16 h-16 mx-auto rounded-full bg-orange-100 flex items-center justify-center">
              <AlertTriangle className="w-8 h-8 text-orange-600" />
            </div>
            <h1 className="font-display font-extrabold text-2xl text-[#1A1A1A] mt-5">
              {isExpired ? t("supplierCompletion.linkExpiredTitle") : t("supplierCompletion.linkInvalidTitle")}
            </h1>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">
              {isExpired ? t("supplierCompletion.linkExpired") : t("supplierCompletion.linkInvalid")}
            </p>
          </motion.div>
        </div>
      </PublicShell>
    );
  }

  if (success) {
    return (
      <PublicShell>
        <div className="max-w-md mx-auto px-6 py-20">
          <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
            data-testid="completion-success"
            className="bg-white rounded-3xl border border-slate-200 p-8 text-center">
            <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-10 h-10 text-emerald-600" strokeWidth={2} />
            </div>
            <h1 className="font-display font-extrabold text-2xl text-[#1A1A1A] mt-5">{t("supplierCompletion.successTitle")}</h1>
            <p className="text-sm text-slate-500 mt-2 leading-relaxed">{t("supplierCompletion.successBody")}</p>
          </motion.div>
        </div>
      </PublicShell>
    );
  }

  const TypeIcon = isServicios ? Wrench : Package;
  const typeLabel = isServicios ? t("suppliers.typeServicios") : t("suppliers.typeInsumos");

  return (
    <PublicShell>
      <div className="max-w-2xl mx-auto px-6 py-12">
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <span className="inline-block bg-[#FFDF00] text-[#1A1A1A] px-3 py-1 rounded-full text-[10px] uppercase tracking-wider font-bold mb-3">
            {t("supplierCompletion.stepOf", { n: step })}
          </span>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-[#1A1A1A]">{pre.business_name}</h1>
          <div className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700"
            data-testid="completion-type-chip">
            <TypeIcon className="w-3.5 h-3.5" /> {typeLabel}
          </div>
        </motion.div>

        <div className="mt-8 bg-white rounded-2xl border border-slate-200 p-6 sm:p-8 space-y-5">
          <AnimatePresence mode="wait">
            {step === 1 && (
              <motion.div key="c1" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} className="space-y-4">
                <h2 className="font-display font-bold text-lg">{t("supplierCompletion.welcomeTitle")}</h2>
                <p className="text-sm text-slate-600 leading-relaxed">{t("supplierCompletion.welcomeBody")}</p>
                <p className="text-sm text-slate-500 leading-relaxed">
                  {t("supplierCompletion.registeringAs")} <strong className="text-[#1A1A1A]">{typeLabel}</strong>.
                </p>
                <div className="bg-[#FFFBE6] border border-[#FFDF00] rounded-xl p-3 text-xs text-slate-700">
                  {t("supplierCompletion.welcomeIntro")}
                </div>
                <button data-testid="completion-start-btn" onClick={() => setStep(2)}
                  className="btn-primary-chizy px-5 py-2.5 rounded-xl text-sm flex items-center gap-2">
                  {t("supplierCompletion.startBtn")} <ArrowRight className="w-4 h-4" />
                </button>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div key="c2" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} className="space-y-4">
                <h2 className="font-display font-bold text-lg">{t("supplierReg.taxData")}</h2>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.businessName")}</label>
                  <input data-testid="completion-business-name" value={pre.business_name} readOnly disabled
                    className="mt-1.5 w-full px-4 py-2.5 bg-slate-100 border border-slate-200 rounded-xl text-sm text-slate-600 cursor-not-allowed" />
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.rfc")}</label>
                    <input data-testid="completion-rfc" value={form.rfc}
                      onChange={(e) => setForm({ ...form, rfc: e.target.value.toUpperCase() })}
                      placeholder="ABC123456XY7" maxLength={13}
                      className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.categoryHint")}</label>
                    <input data-testid="completion-category" value={form.category} list="completion-category-options"
                      onChange={(e) => setForm({ ...form, category: e.target.value })}
                      placeholder={isServicios ? t("supplierReg.categoryServiciosPh") : t("supplierReg.categoryInsumosPh")}
                      className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                    <datalist id="completion-category-options">
                      {(CATEGORY_SUGGESTIONS[pre.supplier_type] || []).map((c) => <option key={c} value={c} />)}
                    </datalist>
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.address")}</label>
                  <input data-testid="completion-address" value={form.tax_address}
                    onChange={(e) => setForm({ ...form, tax_address: e.target.value })}
                    placeholder={t("supplierReg.addressPh")}
                    className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setStep(1)} className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">{t("supplierReg.back")}</button>
                  <button data-testid="completion-step2-next" onClick={() => setStep(3)}
                    disabled={!form.rfc || !form.tax_address}
                    className="btn-primary-chizy px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50">
                    {t("common.next")} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div key="c3" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} className="space-y-4">
                <h2 className="font-display font-bold text-lg">{t("supplierReg.contactPayments")}</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.emailRequired")}</label>
                    <input data-testid="completion-email" type="email" value={form.contact_email}
                      onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                      placeholder="ventas@miempresa.mx"
                      className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.phoneRequired")}</label>
                    <input data-testid="completion-phone" value={form.contact_phone}
                      onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                      placeholder={t("supplierReg.phonePh")}
                      className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.bank")}</label>
                    <input data-testid="completion-bank" value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                      placeholder={t("supplierReg.bankPh")}
                      className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                  </div>
                  <div>
                    <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.clabe")}</label>
                    <input data-testid="completion-clabe" value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
                      maxLength={18} placeholder="012180000000000000"
                      className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                  </div>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.creditDays")}</label>
                  <input data-testid="completion-credit-days" type="number" min="0" max="120" value={form.credit_days}
                    onChange={(e) => setForm({ ...form, credit_days: parseInt(e.target.value) || 0 })}
                    className="mt-1.5 w-full px-4 py-2.5 bg-white border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00] focus:border-[#FFDF00]" />
                </div>

                {needsServiceFields && (
                  <div className="space-y-4 pt-2 border-t border-slate-100" data-testid="completion-service-fields">
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.serviceModality")} <span className="text-red-600">*</span></label>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {MODALITY_OPTIONS.map((m) => (
                          <label key={m} data-testid={`completion-modality-${m}`}
                            className={`flex items-center gap-2 px-3 py-2.5 rounded-xl border cursor-pointer text-sm ${
                              form.service_modality === m ? "border-[#FFDF00] bg-[#FFFBE6] font-semibold" : "border-slate-200 hover:bg-slate-50"
                            }`}>
                            <input type="radio" name="service_modality" value={m} checked={form.service_modality === m}
                              onChange={() => setForm({ ...form, service_modality: m })}
                              className="w-4 h-4 text-[#FFDF00] focus:ring-[#FFDF00]" />
                            {t(`supplierReg.modalityOptions.${m}`)}
                          </label>
                        ))}
                      </div>
                    </div>
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierReg.serviceResponseTime")} <span className="text-red-600">*</span></label>
                      <div className="mt-2 flex flex-wrap gap-2">
                        {RESPONSE_OPTIONS.map((rt) => (
                          <label key={rt} data-testid={`completion-response-${rt}`}
                            className={`flex items-center gap-2 px-3 py-2 rounded-xl border cursor-pointer text-sm ${
                              form.service_response_time === rt ? "border-[#FFDF00] bg-[#FFFBE6] font-semibold" : "border-slate-200 hover:bg-slate-50"
                            }`}>
                            <input type="radio" name="service_response_time" value={rt} checked={form.service_response_time === rt}
                              onChange={() => setForm({ ...form, service_response_time: rt })}
                              className="w-4 h-4 text-[#FFDF00] focus:ring-[#FFDF00]" />
                            {t(`supplierReg.responseTimeOptions.${rt}`)}
                          </label>
                        ))}
                      </div>
                    </div>
                  </div>
                )}

                {multiBranch && (
                  <div className="space-y-4 pt-2 border-t border-slate-100" data-testid="completion-branch-ops-section">
                    <div>
                      <label className="text-[11px] font-bold uppercase tracking-wider text-[#1A1A1A]">{t("supplierCompletion.branchOpsQuestion")}</label>
                      <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
                        <button type="button" data-testid="completion-branch-ops-same" onClick={() => setOperatesDifferently(false)}
                          className={`px-3 py-2.5 rounded-xl border text-sm font-semibold ${operatesDifferently === false ? "border-[#FFDF00] bg-[#FFFBE6]" : "border-slate-200 hover:bg-slate-50"}`}>
                          {t("supplierCompletion.branchOpsSame")}
                        </button>
                        <button type="button" data-testid="completion-branch-ops-different" onClick={() => setOperatesDifferently(true)}
                          className={`px-3 py-2.5 rounded-xl border text-sm font-semibold ${operatesDifferently === true ? "border-[#FFDF00] bg-[#FFFBE6]" : "border-slate-200 hover:bg-slate-50"}`}>
                          {t("supplierCompletion.branchOpsDifferent")}
                        </button>
                      </div>
                    </div>
                    {operatesDifferently && (
                      <div className="space-y-3">
                        {pre.branches.map((b) => (
                          <div key={b.code} className="p-3.5 rounded-xl border border-slate-200 bg-slate-50 space-y-2" data-testid={`completion-branch-op-${b.code}`}>
                            <div className="text-xs font-bold text-[#1A1A1A]">{b.name} ({b.code})</div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                              <input data-testid={`completion-branch-op-vendor-${b.code}`} placeholder={t("supplierCompletion.branchOpsFields.vendor_name")}
                                value={branchOpsForm[b.code]?.vendor_name || ""} onChange={(e) => setBranchOpField(b.code, "vendor_name", e.target.value)}
                                className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                              <input data-testid={`completion-branch-op-email-${b.code}`} type="email" placeholder={t("supplierCompletion.branchOpsFields.orders_email")}
                                value={branchOpsForm[b.code]?.orders_email || ""} onChange={(e) => setBranchOpField(b.code, "orders_email", e.target.value)}
                                className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                              <input data-testid={`completion-branch-op-bank-${b.code}`} placeholder={t("supplierCompletion.branchOpsFields.bank_name")}
                                value={branchOpsForm[b.code]?.bank_name || ""} onChange={(e) => setBranchOpField(b.code, "bank_name", e.target.value)}
                                className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                              <input data-testid={`completion-branch-op-clabe-${b.code}`} maxLength={18} placeholder={t("supplierCompletion.branchOpsFields.bank_clabe")}
                                value={branchOpsForm[b.code]?.bank_clabe || ""} onChange={(e) => setBranchOpField(b.code, "bank_clabe", e.target.value)}
                                className="px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm font-mono focus:outline-none focus:ring-2 focus:ring-[#FFDF00]" />
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex gap-2">
                  <button onClick={() => setStep(2)} className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">{t("supplierReg.back")}</button>
                  <button data-testid="completion-step3-next" onClick={() => setStep(4)}
                    disabled={!form.contact_email || !form.contact_phone || !form.bank_name || !form.bank_account || !serviceFieldsOk || !branchOpsOk}
                    className="btn-primary-chizy px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50">
                    {t("common.next")} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div key="c4" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} className="space-y-4">
                <h2 className="font-display font-bold text-lg">{t("supplierReg.docsTitle")}</h2>
                <p className="text-sm text-slate-500">{t("supplierReg.docsSubtitle")}</p>
                <div className="space-y-3">
                  {DOC_FIELDS.map((f) => {
                    const st = docs[f.type];
                    return (
                      <div key={f.type} className="border border-slate-200 rounded-xl p-3.5" data-testid={`completion-doc-${f.type}`}>
                        <div className="flex items-center justify-between gap-3 mb-2">
                          <div className="text-sm font-semibold text-[#1A1A1A]">
                            {t(`supplierReg.docLabels.${f.type}`)}
                            {f.required
                              ? <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-red-600">{t("supplierReg.docRequired")}</span>
                              : <span className="ml-2 text-[10px] uppercase tracking-wider font-bold text-slate-400">{t("supplierReg.docOptional")}</span>}
                          </div>
                          {st?.id && <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />}
                        </div>
                        {f.hint && <p className="text-xs text-slate-500 mb-2">{t(`supplierReg.docHints.${f.type}`)}</p>}
                        <label className="flex items-center gap-2 cursor-pointer">
                          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 rounded-lg text-xs font-bold">
                            <Upload className="w-3.5 h-3.5" /> {t("supplierReg.chooseFile")}
                          </span>
                          <span className="text-xs text-slate-500 truncate">
                            {st?.uploading ? t("supplierReg.uploading") : (st?.file?.name || t("supplierReg.noFile"))}
                          </span>
                          <input data-testid={`completion-doc-input-${f.type}`} type="file" accept="application/pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,.heic,.heif" className="hidden"
                            onChange={(e) => handleDocChange(f.type, e.target.files?.[0])} />
                        </label>
                      </div>
                    );
                  })}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setStep(3)} className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">{t("supplierReg.back")}</button>
                  <button data-testid="completion-step4-next" onClick={() => setStep(5)}
                    disabled={!mandatoryUploaded}
                    className="btn-primary-chizy px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50">
                    {t("common.next")} <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </motion.div>
            )}

            {step === 5 && (
              <motion.div key="c5" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -8 }} className="space-y-4">
                <h2 className="font-display font-bold text-lg">{t("supplierCompletion.reviewTitle")}</h2>
                <div className="bg-slate-50 rounded-xl p-4 max-h-64 overflow-y-auto text-xs leading-relaxed text-slate-700 space-y-2">
                  <p><strong>{t("supplierReg.ndaTitle")} — Chizy Chiz Pizza</strong></p>
                  <p>{t("supplierReg.ndaBody1")}</p>
                  <p>{t("supplierReg.ndaBody2")}</p>
                  <p>{t("supplierReg.ndaBody3")}</p>
                  <p>{t("supplierReg.ndaBody4")}</p>
                  <p><Trans i18nKey="supplierReg.ndaBody5" components={{ strong: <strong /> }} /></p>
                </div>
                <label className="flex items-start gap-3 cursor-pointer">
                  <input data-testid="completion-nda-check" type="checkbox" checked={form.nda_accepted}
                    onChange={(e) => setForm({ ...form, nda_accepted: e.target.checked })}
                    className="mt-0.5 w-4 h-4 rounded border-slate-300 text-[#FFDF00] focus:ring-[#FFDF00]" />
                  <span className="text-sm text-slate-700">
                    <Trans i18nKey="supplierReg.iAgree" components={{ strong: <strong /> }} />
                  </span>
                </label>
                <div className="bg-[#FFFBE6] border border-[#FFDF00] rounded-xl p-3 flex items-start gap-2 text-xs text-slate-700">
                  <ShieldCheck className="w-4 h-4 text-[#1A1A1A] flex-shrink-0 mt-0.5" />
                  <span>{t("supplierReg.ndaShield")}</span>
                </div>
                {privacyNotice && (
                  <label className="flex items-start gap-3 cursor-pointer border-t border-slate-100 pt-4">
                    <input data-testid="completion-privacy-check" type="checkbox" checked={privacyAccepted}
                      onChange={(e) => setPrivacyAccepted(e.target.checked)}
                      className="mt-0.5 w-4 h-4 rounded border-slate-300 text-[#FFDF00] focus:ring-[#FFDF00]" />
                    <span className="text-sm text-slate-700">
                      {t("supplierReg.privacyLabel", { n: privacyNotice.version_number, date: privacyNotice.effective_date })}{" "}
                      <a href={`${process.env.REACT_APP_BACKEND_URL}${privacyNotice.pdf_url}`} target="_blank" rel="noopener noreferrer"
                        data-testid="completion-privacy-link" className="font-semibold text-[#1A1A1A] underline">
                        {t("supplierReg.privacyView")}
                      </a>
                    </span>
                  </label>
                )}
                <div className="flex gap-2">
                  <button onClick={() => setStep(4)} className="px-5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm font-semibold hover:bg-slate-50">{t("supplierReg.back")}</button>
                  <button data-testid="completion-submit" onClick={submit} disabled={submitting || !form.nda_accepted || (privacyNotice && !privacyAccepted)}
                    className="btn-primary-chizy px-5 py-2.5 rounded-xl text-sm flex items-center gap-2 disabled:opacity-50">
                    {submitting ? t("supplierReg.sending") : t("supplierCompletion.submitBtn")}
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </PublicShell>
  );
}
